import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError, formatDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Paperclip, DownloadSimple, CircleNotch, FileText, FilePdf, Trash } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_LABELS = {
  dokumen_klaim: "Dokumen Klaim Lengkap",
  surat_kuasa: "Surat Kuasa Investigasi",
  gabungan: "Klaim + Surat Kuasa",
};
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const isImage = (d) => (d.content_type || "").startsWith("image/");
const isPdf = (d) => (d.content_type || "") === "application/pdf";
const docUrl = (d) => `${BACKEND}/api/documents/${d.id}/download`;

export default function DocumentsSection({ claim, canUpload }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get(`/claims/${claim.id}/documents`).then((r) => setDocs(r.data))
    .catch((err) => console.error("Gagal memuat dokumen:", err)), [claim.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Ukuran file maksimal 10 MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/claims/${claim.id}/documents`, fd);
      toast.success(`Dokumen "${file.name}" terunggah`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (d) => {
    if (!window.confirm(`Hapus dokumen "${d.original_filename}"?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Dokumen dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canDelete = (d) => user.role === "admin" || d.uploaded_by === user.id;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Dokumen Pendukung</h3>
        {canUpload && (
          <>
            <input ref={inputRef} type="file" className="hidden" data-testid="document-file-input"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4" onChange={upload} />
            <Button data-testid="upload-document-button" size="sm" variant="outline" disabled={uploading}
              onClick={() => inputRef.current?.click()} className="rounded-none h-8 gap-2">
              {uploading ? <CircleNotch size={14} className="animate-spin" /> : <Paperclip size={14} />} Unggah Dokumen
            </Button>
          </>
        )}
      </div>

      {docs.length === 0 ? (
        <p className="p-3 text-sm text-muted-foreground border border-border/60" data-testid="empty-documents">Belum ada dokumen</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {docs.map((d) => (
            <div key={d.id} data-testid={`document-card-${d.id}`} className="border border-border/60 flex flex-col">
              {isImage(d) ? (
                <button type="button" data-testid={`preview-document-${d.id}`} onClick={() => setPreview(d)}
                  className="h-24 w-full overflow-hidden bg-muted">
                  <img src={docUrl(d)} alt={d.original_filename} className="h-24 w-full object-cover hover:opacity-80 transition-opacity duration-150" />
                </button>
              ) : (
                <button type="button" data-testid={`preview-document-${d.id}`} onClick={() => window.open(docUrl(d), "_blank")}
                  className="h-24 w-full flex flex-col items-center justify-center gap-1 bg-muted hover:bg-accent transition-colors duration-150">
                  {isPdf(d) ? <FilePdf size={28} className="text-red-700" /> : <FileText size={28} className="text-muted-foreground" />}
                  <span className="text-[10px] uppercase text-muted-foreground">{(d.original_filename.split(".").pop() || "file")}</span>
                </button>
              )}
              <div className="px-2 py-1.5 flex items-center justify-between gap-1 border-t border-border/60">
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate" title={d.original_filename}>{d.original_filename}</p>
                  {d.category && CATEGORY_LABELS[d.category] && (
                    <span data-testid={`document-category-${d.id}`} className="inline-block mt-0.5 text-[9px] uppercase tracking-wide border border-amber-300 bg-amber-50 text-amber-800 px-1 py-0.5">
                      {CATEGORY_LABELS[d.category]}
                    </span>
                  )}
                  <p className="text-[10px] text-muted-foreground truncate">{fmtSize(d.size)} · {d.uploaded_by_name} · {formatDate(d.created_at)}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a data-testid={`download-document-${d.id}`} href={docUrl(d)} target="_blank" rel="noreferrer"
                    className="text-muted-foreground hover:text-slate-900 transition-colors duration-150"><DownloadSimple size={15} /></a>
                  {canDelete(d) && (
                    <button data-testid={`delete-document-${d.id}`} onClick={() => remove(d)}
                      className="text-muted-foreground hover:text-red-600 transition-colors duration-150"><Trash size={15} /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="text-sm font-mono truncate">{preview?.original_filename}</DialogTitle></DialogHeader>
          {preview && <img src={docUrl(preview)} alt={preview.original_filename} className="max-h-[70vh] w-full object-contain" data-testid="document-preview-image" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
